import { FC, useRef, MouseEvent, useState, useEffect } from "react";
import {
	MoreHorizontal,
	FolderInput,
	CheckSquare,
	Settings,
	Copy,
	Pencil,
	Trash2
} from "lucide-react";
import { adaptFormType } from "src/features/observationsAdmin/utils";

export interface FormCardProps extends Partial<ObservationForm> {
	showCardOptions?: boolean;

	isFormPublished?: boolean;

	onArchiveFormClick?: (e: MouseEvent<HTMLButtonElement>) => void;

	onDuplicateFormClick?: (e: MouseEvent<HTMLButtonElement>) => void;

	onEditFormClick?: (e: MouseEvent<HTMLButtonElement>) => void;

	onMoveToDraftsClick?: (e: MouseEvent<HTMLButtonElement>) => void;

	onPublishFormClick?: (e: MouseEvent<HTMLButtonElement>) => void;

	onSettingsClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const FormCard: FC<FormCardProps> = ({
	name,
	observationType,
	updatedAt,
	updatedBy,
	isFormPublished = false,
	showCardOptions = false,
	onArchiveFormClick,
	onDuplicateFormClick,
	onEditFormClick,
	onMoveToDraftsClick,
	onPublishFormClick,
	onSettingsClick
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleOutsideClick = (event: globalThis.MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleOutsideClick);
		}

		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [isOpen]);

	const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsOpen((prev) => !prev);
	};

	return (
		<div className="group rounded-xl border border-border-light bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-px transition-all duration-200">
			<div className="flex items-start justify-between gap-3">
				<div className="flex-1 min-w-0">
					<h3 className="font-heading text-base font-medium text-zinc-800 line-clamp-2 mb-1">
						{name}
					</h3>

					{observationType && (
						<p className="text-[13px] text-zinc-600 mb-3">
							{adaptFormType(observationType)}
						</p>
					)}

					<div className="flex items-center gap-3 text-[13px] text-zinc-600">
						<span>{updatedAt}</span>
						<span>by {updatedBy ? updatedBy : "Unknown"}</span>
					</div>
				</div>

				{showCardOptions && (
					<div className="relative" ref={menuRef}>
						<button
							onClick={toggleMenu}
							aria-label="menu-button"
							className="rounded-lg p-1.5 hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
						>
							<MoreHorizontal className="h-4 w-4 text-zinc-500" />
						</button>

						{isOpen && (
							<div
								aria-label="menu-options"
								className="absolute right-0 top-full mt-1 w-48 bg-white border border-border rounded-lg shadow-lg z-50 py-1"
							>
								{onEditFormClick && (
									<button
										onClick={onEditFormClick}
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors text-left"
									>
										<Pencil className="h-4 w-4 text-zinc-500" />
										Edit questions
									</button>
								)}

								{isFormPublished && onMoveToDraftsClick && (
									<button
										onClick={onMoveToDraftsClick}
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors text-left"
									>
										<FolderInput className="h-4 w-4 text-zinc-500" />
										Move to drafts
									</button>
								)}

								{!isFormPublished && onPublishFormClick && (
									<button
										onClick={onPublishFormClick}
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors text-left"
									>
										<CheckSquare className="h-4 w-4 text-zinc-500" />
										Publish
									</button>
								)}

								{onDuplicateFormClick && (
									<button
										onClick={onDuplicateFormClick}
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors text-left"
									>
										<Copy className="h-4 w-4 text-zinc-500" />
										Duplicate
									</button>
								)}

								{onArchiveFormClick && (
									<>
										<div className="my-1 border-t border-border" />
										<button
											onClick={onArchiveFormClick}
											className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
										>
											<Trash2 className="h-4 w-4" />
											Delete
										</button>
									</>
								)}

								{onSettingsClick && (
									<button
										onClick={onSettingsClick}
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors text-left"
									>
										<Settings className="h-4 w-4 text-zinc-500" />
										Settings
									</button>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default FormCard;
